import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { MultisiteService } from '@df/core/service/multisite';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ImageModule } from '@df/module-image/image.module';
import { NgInject } from '@df/ng/ng-inject';
import { CustomerModel } from '@df/session/api/customer.model';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { RatingComponent } from '@df/ui/common/rating.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { MultisiteLabelComponent } from '../../../multisite/label/multisite-label.component';
import { NMultisiteModal } from '../../../multisite/modal/multisite-modal.interfaces';
import { MultisiteSwitchComponent } from '../../../multisite/switch/multisite-switch.component';
import { IfNotTradeStoreDirective } from '../../../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../../../multisite/trade/if-trade-store.directive';
import { TradeClassDirective } from '../../../multisite/trade/trade-class.directive';
import { TradeStoreService } from '../../../multisite/trade/trade-store.service';
import { NewsletterFormComponent } from '../../../newsletter/newsletter-form.component';
import { BottomPanelAreaDirective } from '../../../ui/widget/bottom-panel/bottom-panel-area.directive';
import { PageFooterColumnMobileComponent } from './page-footer-column-mobile.component';
import { PageFooterColumnComponent } from './page-footer-column.component';
import { PageFooterVisibilityService } from './page-footer-visibility.service';

export interface IPageFooterBlockData extends Catalog.IBlockData<IPageFooterBlockContent> {}
export interface IPageFooterBlockContent {
    newsletter_heading: string;
    newsletter_copy: string;
    newsletter_checkbox: string;
    social_services: IPageFooterSocialLink[];
    columns: IPageFooterColumn[];
    columns_mobile: IPageFooterColumnMobile[];
    contact_phone: string;
    contact_mail: string;
    links: {
        text: string;
        link?: string;
    }[];
    payment_method_icons?: IPageFooterPaymentIcon[];
    reviews_rating?: number;
}
export interface IPageFooterColumn {
    heading: true;
    links: {
        label: string;
        link?: string;
    }[];
}
export interface IPageFooterSocialLink {
    image: string;
    link: string;
}
export interface IPageFooterColumnMobile {
    type: string;
    heading: string;
    links: {
        label: string;
        link?: string;
    }[];
}
export interface IPageFooterPaymentIcon {
    image: string;
}

@Component({
    selector: 'page-footer',
    templateUrl: 'page-footer.html',
    standalone: true,
    imports: [
        BottomPanelAreaDirective,
        DfUiModule,
        GridModule,
        HrefComponent,
        ICmsClientModule,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        IfNotTradeStoreDirective,
        IfTradeStoreDirective,
        ImageModule,
        MultisiteLabelComponent,
        MultisiteSwitchComponent,
        NewsletterFormComponent,
        NgClass,
        NgTemplateOutlet,
        PageFooterColumnComponent,
        PageFooterColumnMobileComponent,
        RatingComponent,
        SizeClassDirective,
        TradeClassDirective,
        TranslationModule,
        ZippyModule
    ],
    styleUrls: ['./page-footer.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageFooterComponent>({
    id: 'page-footer',
    block: {
        url: 'page-footer',
        autoload: 'prerender',
        scope: 'website'
    },
    onDemandOnly: true,
    name: 'Page footer',
    description: 'Page footer component',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-newsletter',
            title: 'Newsletter'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'newsletter_heading',
            title: 'Newsletter heading',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'newsletter_copy',
            title: 'Newsletter copy',
            required: true
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'newsletter_checkbox',
            title: 'Newsletter checkbox text',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'social_services',
            title: 'Social services',
            itemName: 'social service',
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Name',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link',
                    required: true
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'columns',
            title: 'Columns',
            itemName: 'column',
            maxSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'heading',
                    title: 'Column heading',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Column links',
                    itemName: 'link',
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'label',
                            title: 'Label',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'link',
                            title: 'Link'
                        }
                    ]
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'columns_mobile',
            title: 'Columns mobile',
            itemName: 'Column',
            itemNameField: 'column',
            maxSize: 3,
            canShow: () => window['app']?.client?.isS,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'type',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Type',
                    options: [
                        { key: 'links', title: 'Links' },
                        { key: 'multisite', title: 'Multisite' }
                    ],
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'heading',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Heading',
                    required: true,
                    canShow: (data: IPageFooterColumnMobile) => data.type === 'links'
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'link',
                    canShow: (data: IPageFooterColumnMobile) => data.type === 'links',
                    fields: [
                        <NICmsContentField.IField>{
                            key: 'label',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Text',
                            required: true
                        },
                        <NICmsContentField.IField>{
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link'
                        }
                    ]
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'contact_label',
            title: 'Contact'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'contact_phone',
            title: 'Phone',
            default: '+44 2380 814 360',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'contact_mail',
            title: 'Mail',
            default: 'info@horsehealth.co.uk',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'payment_method_icons',
            title: 'Payment Method icons',
            itemName: 'payment method',
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Payment service',
                    required: true
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'links',
            title: 'Bottom Links',
            itemName: 'Item',
            itemNameField: 'text',
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Label',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                }
            ]
        },
        <NICmsContentField.INumberField>{
            type: NICmsContentField.EFieldType.number,
            key: 'reviews_rating',
            title: 'Reviews rating',
            min: 0,
            max: 100,
            description: '0-100'
        }
    ]
})
export class PageFooterComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content: IPageFooterBlockContent;

    @NgInject(CustomerModel)
    customer: CustomerModel;
    @NgInject(PageFooterVisibilityService)
    readonly pageFooterVisibilityService: PageFooterVisibilityService;

    protected readonly multisiteModalId = NMultisiteModal.componentId;

    @NgInject(TradeStoreService)
    private tradeStoreService: TradeStoreService;

    @NgInject(MultisiteService)
    private multisiteService: MultisiteService;

    isHidden = false;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.customer.obs.data.subscribe(() => this.update()));
        this.subscriptions.add(this.pageFooterVisibilityService.observe.subscribe(state => this.updateVisibility(state)));
    }
    update() {
        this.detectChanges();
    }

    private updateVisibility(isHidden: boolean) {
        if (isHidden !== this.isHidden) {
            this.isHidden = isHidden;
            this.detectChanges();
        }
    }

    get hideMultisteRetailSelector(): boolean {
        return !this.tradeStoreService.retailSites.filter(site => site.siteId !== this.multisiteService.current.siteId)?.length;
    }
}
